/**
 * Tags (frames 86:2, 304:11029, 632:19479 states 1-2, 5): one row -- a removable chip per
 * `(category, value)` pair, each labelled `<category>: <value>`, then a `+ tag` that opens a
 * combobox popover.
 *
 * The popover has two steps. The key step lists the campaign's active categories (most-used
 * first, including ones already on the account -- picking one just opens its value step to add
 * another value) and offers to create a new one; creating moves straight to the value step. The
 * value step lists that category's values and offers to create one. Typing `chave: valor` in the
 * key step pins the key (found by name, or a brand-new one with no id yet) and switches straight
 * to the value step with the rest of the text as the value query, so Enter on the resulting
 * "Criar" item creates whichever half is missing in one round trip. A deactivated category is
 * never listed, but a value the account already carries from one keeps rendering as a chip --
 * `props.value` is never filtered by `active`, only the key step's own list is.
 *
 * The value-step search row embeds the pinned category as a chip ahead of the actual text input
 * (632:19479 state 2's "Partido" chip) -- `ui/command.tsx`'s `CommandInput` has no such slot, so
 * this reaches `cmdk`'s bare `Command.Input` directly rather than hand-rolling the whole listbox.
 *
 * The key step reads `useTagCategories`, which already carries each category's `values` nested
 * (`api`'s `TagCategoryOut`) -- there is no separate value-list request, so the value step reads
 * straight from that same cache. Every category and value renders with its icon (its own, else
 * its category's). The locked alignment category is listed only when filtering: an account's
 * alignment is chosen on its own field.
 */
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { interpolatePath } from '@tanstack/react-router';
import { ExternalLink, Filter, Plus } from 'lucide-react';

import { defineCatalogue } from '../../../i18n';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../ui/command';
import { FieldSet, FieldLegend } from '../../../ui/field';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { TagGlyph, tagIconName } from '../../tenant/tags/icons';
import { useCreateTagCategory, useCreateTagValue, useTagCategories } from './api/queries';
import type { TagCategory, TagPair, TagValue } from './api/schemas';

const tagFieldCatalogue = defineCatalogue(
  {
    label: 'Tags',
    manage: 'Gerir tags da campanha',
    addTag: 'tag',
    addFilter: 'Filtrar',
    // Trailing space is load-bearing: it separates the muted category span from the plain-text
    // value that follows it with no whitespace in the JSX source (a lint rule refuses bare JSX
    // text, so the separator lives in the template instead of a literal `{' '}`).
    chipCategory: '{category}: ',
    searchPlaceholder: 'Buscar ou criar · chave: valor',
    valueSearchPlaceholder: 'Buscar ou criar',
    filterSearchPlaceholder: 'Buscar',
    createCategory: 'Criar a tag "{name}"',
    createValue: 'Criar "{name}" em {category}',
    'valuesCount.one': '{count} valor',
    'valuesCount.other': '{count} valores',
    removeTag: 'Remover {tag}',
    categoriesLoading: 'Carregando tags…',
    categoriesError: 'Não foi possível carregar as tags da campanha.',
    createError: 'Não foi possível criar.',
  },
  {
    label: 'Tags',
    manage: 'Manage the campaign’s tags',
    addTag: 'tag',
    addFilter: 'Filter',
    chipCategory: '{category}: ',
    searchPlaceholder: 'Search or create · key: value',
    valueSearchPlaceholder: 'Search or create',
    filterSearchPlaceholder: 'Search',
    createCategory: 'Create the tag "{name}"',
    createValue: 'Create "{name}" in {category}',
    'valuesCount.one': '{count} value',
    'valuesCount.other': '{count} values',
    removeTag: 'Remove {tag}',
    categoriesLoading: 'Loading tags…',
    categoriesError: 'The campaign’s tags could not be loaded.',
    createError: 'This could not be created.',
  },
);
const tft = tagFieldCatalogue.t;
const tfp = tagFieldCatalogue.plural;

export type { TagPair };

/** `tenant/routes.ts`'s own `TAGS_PATH`, not re-exported from there -- see this area's
 *  `paths.ts` for why a route literal used from outside its owning file is built once rather
 *  than redeclared. A plain anchor, not `Link`: `interpolatePath` needs no route match, so this
 *  field stays usable wherever it is embedded, with no dependency on the caller's router state. */
function tagsManagementHref(campaignId: string): string {
  return interpolatePath({ path: '/c/$campaignId/tags', params: { campaignId } }).interpolatedPath;
}

type PendingCategory = { id: string | null; name: string };

export function TagField(props: {
  campaignId: string;
  value: TagPair[];
  onChange: (value: TagPair[]) => void;
  /** `filter` picks from what exists only: no creating, no legend, no management link. */
  mode?: 'edit' | 'filter';
}): ReactNode {
  const filtering = props.mode === 'filter';
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'category' | 'value'>('category');
  const [query, setQuery] = useState('');
  const [pendingCategory, setPendingCategory] = useState<PendingCategory | null>(null);

  // A filter's chips name vocabulary values from the URL, so it needs the vocabulary before opening.
  const categories = useTagCategories(props.campaignId, open || filtering);
  // The alignment is chosen on its own field when adding an account, and filtered on here.
  const listed = (categories.data ?? []).filter((category) => filtering || !category.locked);
  const iconOf = (value: TagValue | null, categoryId: string | null): string =>
    tagIconName(
      value,
      listed.find((category) => category.id === categoryId),
    );
  // `pendingCategory.id` is `null` for a not-yet-created category, which matches no fetched row.
  const pendingCategoryValues: TagValue[] =
    listed.find((category) => category.id === pendingCategory?.id)?.values ?? [];
  const createCategory = useCreateTagCategory(props.campaignId);
  const createValue = useCreateTagValue(props.campaignId);

  function resetToStep(next: 'category' | 'value'): void {
    setStep(next);
    setQuery('');
    if (next === 'category') setPendingCategory(null);
    createCategory.reset();
    createValue.reset();
  }

  function openPicker(): void {
    resetToStep('category');
    setOpen(true);
  }

  function addPair(pair: TagPair): void {
    if (!props.value.some((p) => p.valueId === pair.valueId)) {
      // A single-value category (e.g. "Atenção da equipe") swaps rather than adds, when editing.
      const single =
        !filtering && listed.find((category) => category.name === pair.category)?.single === true;
      const kept = single ? props.value.filter((p) => p.category !== pair.category) : props.value;
      props.onChange([...kept, pair]);
    }
    setOpen(false);
    resetToStep('category');
  }

  function removePair(pair: TagPair): void {
    props.onChange(props.value.filter((p) => p.valueId !== pair.valueId));
  }

  function pinCategory(name: string, rest: string): void {
    const existing =
      listed.find((category) => category.name.toLowerCase() === name.toLowerCase()) ?? null;
    if (existing === null && filtering) return;
    setPendingCategory(existing ? { id: existing.id, name: existing.name } : { id: null, name });
    setStep('value');
    setQuery(rest);
  }

  function onCategoryQueryChange(next: string): void {
    const colon = next.indexOf(':');
    if (colon > 0) {
      const name = next.slice(0, colon).trim();
      if (name !== '') {
        pinCategory(name, next.slice(colon + 1).replace(/^\s+/, ''));
        return;
      }
    }
    setQuery(next);
  }

  function chooseCategory(category: TagCategory): void {
    setPendingCategory({ id: category.id, name: category.name });
    setStep('value');
    setQuery('');
  }

  async function createCategoryFlow(): Promise<void> {
    const name = query.trim();
    if (name === '') return;
    try {
      const created = await createCategory.mutateAsync({ name });
      setPendingCategory({ id: created.id, name: created.name });
      setStep('value');
      setQuery('');
    } catch {
      // Surfaced via `createCategory.isError` below; the typed text stays.
    }
  }

  function selectValue(value: TagValue): void {
    addPair({
      category: pendingCategory!.name,
      value: value.name,
      valueId: value.id,
      icon: iconOf(value, pendingCategory!.id),
    });
  }

  async function confirmValue(): Promise<void> {
    const text = query.trim();
    if (text === '' || pendingCategory === null) return;
    try {
      let categoryId = pendingCategory.id;
      let categoryName = pendingCategory.name;
      if (categoryId === null) {
        const created = await createCategory.mutateAsync({ name: categoryName });
        categoryId = created.id;
        categoryName = created.name;
      }
      const created = await createValue.mutateAsync({ categoryId, value: text });
      addPair({
        category: categoryName,
        value: created.name,
        valueId: created.id,
        icon: iconOf(created, categoryId),
      });
    } catch {
      // Surfaced via `createCategory.isError` / `createValue.isError` below; the typed text stays.
    }
  }

  function onValueSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace' && query === '') resetToStep('category');
  }

  const trimmedQuery = query.trim();
  const showCreateCategory =
    !filtering &&
    step === 'category' &&
    trimmedQuery !== '' &&
    !categories.data?.some(
      (category) => category.name.toLowerCase() === trimmedQuery.toLowerCase(),
    );
  const showCreateValue =
    !filtering &&
    step === 'value' &&
    trimmedQuery !== '' &&
    !pendingCategoryValues.some((value) => value.name.toLowerCase() === trimmedQuery.toLowerCase());

  const picker = (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetToStep('category');
      }}
    >
      <PopoverAnchor asChild>
        <div
          className={
            filtering
              ? 'flex min-h-9 flex-wrap items-center gap-1.5'
              : 'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input px-3 py-1.5'
          }
        >
          {props.value.map((pair) => (
            <Chip
              key={pair.valueId}
              tone="outline"
              icon={pair.icon ? <TagGlyph name={pair.icon} /> : undefined}
              onClose={() => removePair(pair)}
              closeLabel={tft('removeTag', { tag: `${pair.category}: ${pair.value}` })}
            >
              {pair.category !== '' && (
                <span className="text-muted-foreground">
                  {tft('chipCategory', { category: pair.category })}
                </span>
              )}
              {pair.value}
            </Chip>
          ))}
          <PopoverTrigger asChild>
            {filtering ? (
              <Button type="button" variant="outline" size="sm" onClick={openPicker}>
                <Filter aria-hidden />
                {tft('addFilter')}
              </Button>
            ) : (
              <Button type="button" variant="ghost" size="xs" onClick={openPicker}>
                <Plus aria-hidden />
                {tft('addTag')}
              </Button>
            )}
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter>
          {step === 'category' ? (
            <>
              <CommandInput
                value={query}
                onValueChange={onCategoryQueryChange}
                placeholder={tft(filtering ? 'filterSearchPlaceholder' : 'searchPlaceholder')}
              />
              <CommandList>
                {categories.isPending && listed.length === 0 && (
                  <p className="p-4 text-sm text-muted-foreground">{tft('categoriesLoading')}</p>
                )}
                {categories.isError && (
                  <p className="p-4 text-sm text-destructive">{tft('categoriesError')}</p>
                )}
                {listed.length > 0 && (
                  <CommandGroup>
                    {listed.map((category) => (
                      <CommandItem
                        key={category.id}
                        value={category.name}
                        onSelect={() => chooseCategory(category)}
                      >
                        <TagGlyph name={tagIconName(null, category)} className="size-4" />
                        {category.name}
                        <span className="ml-auto text-muted-foreground">
                          {tfp('valuesCount.other', category.values.length, {
                            count: category.values.length,
                          })}
                        </span>
                      </CommandItem>
                    ))}
                    {showCreateCategory && (
                      <CommandItem
                        value={`__create-tag__${trimmedQuery}`}
                        onSelect={() => void createCategoryFlow()}
                      >
                        <Plus aria-hidden />
                        {tft('createCategory', { name: trimmedQuery })}
                      </CommandItem>
                    )}
                  </CommandGroup>
                )}
                {createCategory.isError && (
                  <p className="px-3 pb-3 text-sm text-destructive">{tft('createError')}</p>
                )}
              </CommandList>
            </>
          ) : (
            <>
              <div className="flex h-9 items-center gap-1.5 border-b px-3">
                <Chip tone="neutral" size="sm">
                  {pendingCategory!.name}
                </Chip>
                <CommandPrimitive.Input
                  value={query}
                  onValueChange={setQuery}
                  onKeyDown={onValueSearchKeyDown}
                  placeholder={tft(
                    filtering ? 'filterSearchPlaceholder' : 'valueSearchPlaceholder',
                  )}
                  className="h-9 flex-1 bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
                />
              </div>
              <CommandList>
                {pendingCategory!.id === null ? (
                  <CommandGroup>
                    {showCreateValue && (
                      <CommandItem
                        value={`__create-value__${trimmedQuery}`}
                        onSelect={() => void confirmValue()}
                      >
                        <Plus aria-hidden />
                        {tft('createValue', {
                          name: trimmedQuery,
                          category: pendingCategory!.name,
                        })}
                      </CommandItem>
                    )}
                  </CommandGroup>
                ) : (
                  <CommandGroup>
                    {pendingCategoryValues.map((value) => (
                      <CommandItem
                        key={value.id}
                        value={value.name}
                        onSelect={() => selectValue(value)}
                      >
                        <TagGlyph name={iconOf(value, pendingCategory!.id)} className="size-4" />
                        {value.name}
                      </CommandItem>
                    ))}
                    {showCreateValue && (
                      <CommandItem
                        value={`__create-value__${trimmedQuery}`}
                        onSelect={() => void confirmValue()}
                      >
                        <Plus aria-hidden />
                        {tft('createValue', {
                          name: trimmedQuery,
                          category: pendingCategory!.name,
                        })}
                      </CommandItem>
                    )}
                  </CommandGroup>
                )}
                {(createCategory.isError || createValue.isError) && (
                  <p className="px-3 pb-3 text-sm text-destructive">{tft('createError')}</p>
                )}
              </CommandList>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (filtering) return picker;
  return (
    <FieldSet>
      <FieldLegend variant="label" className="flex items-center justify-between gap-2">
        <span>{tft('label')}</span>
        <a
          href={tagsManagementHref(props.campaignId)}
          className="inline-flex items-center gap-1 text-sm font-normal text-muted-foreground hover:text-foreground"
        >
          {tft('manage')}
          <ExternalLink aria-hidden className="size-3.5" />
        </a>
      </FieldLegend>
      {picker}
    </FieldSet>
  );
}
